<template>
  <div
    id="col-left"
    class="col-left--box hw-sidebar"
  >
    <div class="f-head">
      <span class="f-title">{{ t("filters") }}</span>
      <button class="f-reset" type="button" @click="resetFilters">
        {{ $func.parseIso("resetFilters") }}
      </button>
    </div>

    <div class="chips" v-if="chips.length">
      <div class="chip" v-for="(chip, idx) in chips" :key="idx">
        {{ chip.label }}
        <button type="button" @click="removeChip(chip)">×</button>
      </div>
    </div>

    <!-- Destino: facet con conteos, no un paso previo -->
    <div class="fc" v-if="destOptions.length">
      <div class="fc-title" @click="toggle('dest')">
        {{ t("holidayDestination") }}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      <div class="fc-body" v-show="open.dest">
        <div class="fopt" v-for="opt in destOptions" :key="opt.city + opt.country">
          <input
            type="checkbox"
            :id="'hw-dest-' + opt.city"
            :value="opt.city"
            v-model="local.city"
            @change="apply"
          />
          <label :for="'hw-dest-' + opt.city">{{ opt.city }}</label>
          <span class="fcnt">{{ opt.total }}</span>
        </div>
      </div>
    </div>

    <!-- Holiday: solo en busqueda "Any holiday" -->
    <div class="fc" v-if="mode === 'all' && holidayOptions.length">
      <div class="fc-title" @click="toggle('holiday')">
        {{ t("holiday") }}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      <div class="fc-body" v-show="open.holiday">
        <div class="fopt" v-for="opt in holidayOptions" :key="opt.value">
          <input
            type="checkbox"
            :id="'hw-hol-' + opt.value"
            :value="opt.value"
            v-model="local.holidayTokens"
            @change="apply"
          />
          <label :for="'hw-hol-' + opt.value">{{ opt.label }}</label>
          <span class="fcnt">{{ opt.total }}</span>
        </div>
      </div>
    </div>

    <!-- Check-in: solo en holiday especifico -->
    <div class="fc" v-if="mode === 'specific' && checkInOptions.length">
      <div class="fc-title" @click="toggle('checkin')">
        {{ t("hwCheckIn") }}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      <div class="fc-body" v-show="open.checkin">
        <div class="fopt" v-for="opt in checkInOptions" :key="opt.value">
          <input
            type="checkbox"
            :id="'hw-ci-' + opt.value"
            :value="opt.value"
            v-model="local.checkInDates"
            @change="apply"
          />
          <label :for="'hw-ci-' + opt.value">{{ formatDate(opt.value) }}</label>
          <span class="fcnt">{{ opt.total }}</span>
        </div>
      </div>
    </div>

    <!-- Guests: pills 2+/4+/6+/8+ -->
    <div class="fc">
      <div class="fc-title" @click="toggle('guests')">
        {{ $func.parseIso("occupancy") }}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      <div class="fc-body" v-show="open.guests">
        <div class="rpills">
          <button
            type="button"
            class="rpill"
            :class="{ on: local.occupancy === null }"
            @click="setOccupancy(null)"
          >
            {{ t("any") }}
          </button>
          <button
            v-for="n in [2, 4, 6, 8]"
            :key="n"
            type="button"
            class="rpill"
            :class="{ on: local.occupancy === n }"
            @click="setOccupancy(n)"
          >
            {{ n }}+
          </button>
        </div>
      </div>
    </div>

    <!-- Recamaras: facet UnitType (mismo comportamiento que resortlist) -->
    <div class="fc" v-if="bedroomOptions.length">
      <div class="fc-title" @click="toggle('bedrooms')">
        {{ t("hwBedrooms") }}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      <div class="fc-body" v-show="open.bedrooms">
        <div class="fopt" v-for="opt in bedroomOptions" :key="opt.value">
          <input
            type="checkbox"
            :id="'hw-bd-' + opt.value"
            :value="opt.value"
            v-model="local.unitType"
            @change="apply"
          />
          <label :for="'hw-bd-' + opt.value">{{ opt.value }}</label>
          <span class="fcnt">{{ opt.total }}</span>
        </div>
      </div>
    </div>

    <!-- Precio: se mantiene como hoy -->
    <div class="fc">
      <div class="fc-title" @click="toggle('price')">
        {{ t("hwPriceFrom") }}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      <div class="fc-body" v-show="open.price">
        <div class="frange">
          <input
            type="number"
            placeholder="Min"
            v-model.number="local.priceMin"
            @keydown.enter.prevent="apply"
          />
          <span>–</span>
          <input
            type="number"
            placeholder="Max"
            v-model.number="local.priceMax"
            @keydown.enter.prevent="apply"
          />
          <button type="button" class="frange-btn" @click="apply" :aria-label="t('search')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Estrellas: se mantiene como hoy -->
    <div class="fc">
      <div class="fc-title" @click="toggle('rating')">
        {{ t("hwRating") }}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      <div class="fc-body" v-show="open.rating">
        <div class="rpills">
          <button type="button" class="rpill" :class="{ on: local.rating === null }" @click="setRating(null)">
            {{ t("any") }}
          </button>
          <button type="button" class="rpill" :class="{ on: local.rating === '3+' }" @click="setRating('3+')">3.0+</button>
          <button type="button" class="rpill" :class="{ on: local.rating === '4+' }" @click="setRating('4+')">4.0+</button>
          <button type="button" class="rpill" :class="{ on: local.rating === '4.5+' }" @click="setRating('4.5+')">4.5+</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import I18N from "../../utils/i18n_data";
import { holidayLabel } from "../../utils/holidayTags";

export default {
  props: ["search", "facets", "catalog", "mode"],
  data() {
    return {
      local: this.blankLocal(),
      open: {
        dest: true,
        holiday: true,
        checkin: true,
        guests: true,
        bedrooms: false,
        price: false,
        rating: false,
      },
      iso: localStorage.getItem("iso") || "es",
    };
  },
  computed: {
    destOptions() {
      return (this.facets?.ResortDest || [])
        .filter((d) => d.city)
        .map((d) => ({ city: d.city, country: d.country, total: d.total }));
    },
    holidayOptions() {
      const counts = this.facets?.HolidayTags || [];
      const countOf = (token) => (counts.find((c) => c.value === token) || {}).total || 0;
      return (this.catalog?.holidays || [])
        .map((h) => ({
          value: h.token,
          label: `${holidayLabel(h.token, this.iso)} ${h.yearLabel}`,
          total: countOf(h.token),
        }))
        .filter((o) => o.total > 0);
    },
    checkInOptions() {
      return this.facets?.CheckInDates || [];
    },
    bedroomOptions() {
      return this.facets?.UnitType || [];
    },
    chips() {
      const chips = [];
      (this.search?.city || []).forEach((c) => chips.push({ type: "city", value: c, label: c }));
      if (this.mode === "all") {
        (this.search?.holidayTokens || []).forEach((tk) =>
          chips.push({ type: "holidayToken", value: tk, label: holidayLabel(tk, this.iso) })
        );
      } else {
        (this.search?.checkInDates || []).forEach((d) =>
          chips.push({ type: "checkin", value: d, label: this.formatDate(d) })
        );
      }
      if (this.search?.occupancy) chips.push({ type: "occupancy", value: this.search.occupancy, label: this.search.occupancy + "+" });
      (this.search?.unitType || []).forEach((u) => chips.push({ type: "unitType", value: u, label: u }));
      return chips;
    },
  },
  watch: {
    search: {
      handler() {
        this.syncLocal();
      },
      deep: true,
    },
  },
  created() {
    this._onChangeIso = () => {
      this.iso = localStorage.getItem("iso") || "es";
      this.$forceUpdate();
    };
    this.$bus.on("change-iso", this._onChangeIso);
  },
  beforeDestroy() {
    this.$bus.off("change-iso", this._onChangeIso);
  },
  methods: {
    t(key) {
      const entry = I18N[key];
      if (!entry) return key;
      return entry[this.iso] || entry.en;
    },
    blankLocal() {
      return {
        city: [],
        holidayTokens: [],
        checkInDates: [],
        occupancy: null,
        unitType: [],
        priceMin: null,
        priceMax: null,
        rating: null,
      };
    },
    syncLocal() {
      const s = this.search || {};
      this.local = {
        city: [...(s.city || [])],
        // en modo 'all' la seleccion del panel lateral vive aqui
        holidayTokens: s.holiday === "all" ? [...(s.holidayTokens || [])] : [],
        checkInDates: s.holiday !== "all" ? [...(s.checkInDates || [])] : [],
        occupancy: s.occupancy || null,
        unitType: [...(s.unitType || [])],
        priceMin: s.priceMin ?? null,
        priceMax: s.priceMax ?? null,
        rating: s.rating || null,
      };
    },
    toggle(section) {
      this.open[section] = !this.open[section];
    },
    setOccupancy(v) {
      this.local.occupancy = v;
      this.apply();
    },
    setRating(v) {
      this.local.rating = v;
      this.apply();
    },
    formatDate(value) {
      return this.$moment(value).format("ll");
    },
    apply() {
      this.$emit("apply", { ...this.local });
    },
    resetFilters() {
      this.local = this.blankLocal();
      this.$emit("apply", { ...this.local });
    },
    removeChip(chip) {
      if (chip.type === "city") this.local.city = this.local.city.filter((c) => c !== chip.value);
      else if (chip.type === "holidayToken") this.local.holidayTokens = this.local.holidayTokens.filter((c) => c !== chip.value);
      else if (chip.type === "checkin") this.local.checkInDates = this.local.checkInDates.filter((c) => c !== chip.value);
      else if (chip.type === "occupancy") this.local.occupancy = null;
      else if (chip.type === "unitType") this.local.unitType = this.local.unitType.filter((c) => c !== chip.value);
      this.apply();
    },
  },
};
</script>

<style scoped>
.hw-sidebar {
  width: 100%;
}
.f-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 6px;
}
.f-title {
  font-weight: 700;
  font-size: 14px;
  color: #00527c;
}
.f-reset {
  background: none;
  border: 0;
  color: #0472aa;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 16px 10px;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e7f3fa;
  color: #00527c;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
}
.chip button {
  border: 0;
  background: none;
  cursor: pointer;
  color: inherit;
  font-size: 13px;
  padding: 0;
  line-height: 1;
}
.fc {
  border-top: 1px solid #e1e6ea;
  padding: 10px 16px;
}
.fc-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  color: #393939;
}
.fc-title svg {
  width: 14px;
  height: 14px;
  color: #5a6670;
}
.fc-body {
  padding-top: 8px;
  max-height: 280px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c6d2da transparent;
}
.fc-body::-webkit-scrollbar {
  width: 6px;
}
.fc-body::-webkit-scrollbar-thumb {
  background: #c6d2da;
  border-radius: 3px;
}
.fc-body::-webkit-scrollbar-track {
  background: transparent;
}
.fopt {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
  font-size: 13px;
}
.fopt label {
  flex: 1;
  cursor: pointer;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fcnt {
  color: #5a6670;
  font-size: 12px;
}
.rpills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.rpill {
  border: 1px solid #dde3ea;
  background: #fff;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12.5px;
  cursor: pointer;
}
.rpill.on {
  background: #e7f3fa;
  border-color: #0472aa;
  color: #00527c;
  font-weight: 600;
}
.frange {
  display: flex;
  align-items: center;
  gap: 6px;
}
.frange input {
  width: 100%;
  min-width: 0;
  height: 34px;
  border: 1px solid #dde3ea;
  border-radius: 6px;
  padding: 0 8px;
  font-size: 13px;
}
.frange-btn {
  border: 0;
  background: #0472aa;
  color: #fff;
  border-radius: 6px;
  height: 34px;
  width: 34px;
  cursor: pointer;
  display: grid;
  place-items: center;
}
.frange-btn svg {
  width: 14px;
  height: 14px;
}
</style>
